import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import FormHelperText from '@mui/material/FormHelperText';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import React from 'react';
import { useFormContext } from 'react-hook-form';

import { SelectSearchFormField } from '@/components/form/SelectSearchFormField.comp.tsx';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';
import { CreateProcessFormModel } from '@/views/processesCreate/model/CreateProcessForm.model.ts';
import { processCreateDialogStyles as styles } from '@/views/processesCreate/ProcessCreateDialog.styles.ts';
import { ProcessCreateOfferState } from '@/views/processesCreate/util/useProcessCreateOffer.util.ts';

type Props = {
  disabled: boolean;
  offer: ProcessCreateOfferState;
};

/** Offer created with the process: definition and DICTIONARY item that receive the requested entry. */
export const ProcessCreateFormOfferSection: React.FC<Props> = ({ disabled, offer }) => {
  const { t } = useTranslationWithPrefix('views.processesCreate.form.offer');
  const { watch } = useFormContext<CreateProcessFormModel>();
  const selectedDefinition = watch('offerBookingTarget.definition');

  if (offer.status === 'hidden') return null;

  return (
    <Stack component="fieldset" spacing={1} sx={styles.fieldset}>
      <Typography component="legend" variant="f14Medium">
        {t('title')}
      </Typography>
      {offer.status === 'noBooking' && (
        <Alert severity="warning">{t(`noBooking.${offer.reason}`)}</Alert>
      )}
      {offer.status === 'loading' && <CircularProgress aria-label={t('loading')} size={24} />}
      {offer.status === 'error' && <Alert severity="error">{t('definitionsError')}</Alert>}
      {offer.status === 'noDefinitions' && <Alert severity="info">{t('noDefinitions')}</Alert>}
      {offer.status === 'ready' && (
        <>
          <Typography color="textSecondary" variant="f12Regular">
            {t('hint')}
          </Typography>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid size={{ md: 6, xs: 12 }}>
              <SelectSearchFormField<
                CreateProcessFormModel,
                'offerBookingTarget.definition',
                number
              >
                disabled={disabled}
                label={t('definition')}
                name="offerBookingTarget.definition"
                options={offer.definitionOptions}
                placeholder={t('definitionPlaceholder')}
              />
            </Grid>
            <Grid size={{ md: 6, xs: 12 }}>
              <SelectSearchFormField<CreateProcessFormModel, 'offerBookingTarget.item', number>
                disabled={disabled || offer.itemOptions.length <= 1}
                label={t('item')}
                name="offerBookingTarget.item"
                options={offer.itemOptions}
                placeholder={selectedDefinition ? t('itemPlaceholder') : t('chooseDefinitionFirst')}
              />
              {offer.itemOptions.length === 1 && <FormHelperText>{t('singleItem')}</FormHelperText>}
            </Grid>
          </Grid>
        </>
      )}
    </Stack>
  );
};
